<script setup>
import { ref, computed } from 'vue'
import { useDialogStore } from '@/stores/dialogStore'
const router = useRouter()
const dialogStore = useDialogStore()
const agentStore = useAgentStore()
const userStore = useUserStore()
const phoneNumber = ref('')
const verificationCode = ref('')
const isCountingDown = ref(false)
const countdown = ref(60)
const isAgreed = ref(false)
let timer = null

// 聚焦状态变量
const phoneFocused = ref(false)
const codeFocused = ref(false)

const isPhoneNumberValid = computed(() => {
  return /^1[3-9]\d{9}$/.test(phoneNumber.value)
})

const canBind = computed(() => {
  return isPhoneNumberValid.value && verificationCode.value.length === 6 && isAgreed.value
})

async function sendVerificationCode() {
  if (isCountingDown.value || !isPhoneNumberValid.value) return
  if (!isPhoneNumberValid.value) {
    showToast({ message: '请输入有效的手机号' })
    return
  }
  const { data, error } = await useApiFetch('auth/sendSms')
    .post({ mobile: phoneNumber.value, actionType: 'bindMobile' })
    .json()

  if (data.value && !error.value) {
    if (data.value.code === 200) {
      showToast({ message: '获取成功' })
      startCountdown()
    } else {
      showToast(data.value.msg)
    }
  }
}

function startCountdown() {
  isCountingDown.value = true
  countdown.value = 60
  timer = setInterval(() => {
    if (countdown.value > 0) {
      countdown.value--
    } else {
      clearInterval(timer)
      isCountingDown.value = false
    }
  }, 1000)
}

async function handleBind() {
  if (!isPhoneNumberValid.value) {
    showToast({ message: '请输入有效的手机号' })
    return
  }
  if (verificationCode.value.length !== 6) {
    showToast({ message: '请输入有效的验证码' })
    return
  }
  if (!isAgreed.value) {
    showToast({ message: '请先同意用户协议' })
    return
  }

  const { data, error } = await useApiFetch('/user/bindMobile')
    .post({ mobile: phoneNumber.value, code: verificationCode.value })
    .json()

  if (data.value && !error.value) {
    if (data.value.code === 200) {
      showToast({ message: '绑定成功' })
      closeDialog()
      agentStore.fetchAgentStatus()
      userStore.fetchUserInfo()
    } else {
      showToast(data.value.msg)
    }
  }
}

function closeDialog() {
  dialogStore.closeBindPhone()
  // 重置表单
  phoneNumber.value = ''
  verificationCode.value = ''
  isAgreed.value = false
  if (timer) {
    clearInterval(timer)
  }
}

function toUserAgreement() {
  closeDialog()
  router.push(`/userAgreement`)
}

function toPrivacyPolicy() {
  closeDialog()
  router.push(`/privacyPolicy`)
}
</script>

<template>
  <div v-if="dialogStore.showBindPhone">
    <van-popup
      v-model:show="dialogStore.showBindPhone"
      round
      position="bottom"
      :style="{ height: '80%' }"
      @close="closeDialog"
    >
      <div class="bind-phone-dialog">
        <div class="title-bar">
          <div class="font-bold">绑定手机号码</div>
          <div class="text-sm text-gray-500 mt-1">为使用完整功能请绑定手机号码</div>
          <van-icon name="cross" class="close-icon" @click="closeDialog" />
        </div>
        <div class="px-8">
          <div class="mb-8 pt-8 text-left">
            <div class="flex flex-col items-center">
              <img class="h-16 w-16 rounded-full shadow" src="@/assets/images/logo.png" alt="Logo" />
              <div class="text-3xl mt-4 text-slate-700 font-bold">全能查</div>
            </div>
          </div>

          <div class="space-y-5">
            <!-- 手机号输入 -->
            <div :class="['input-container bg-blue-300/20', phoneFocused ? 'focused' : '']">
              <input
                v-model="phoneNumber"
                class="input-field"
                type="tel"
                placeholder="请输入手机号"
                maxlength="11"
                @focus="phoneFocused = true"
                @blur="phoneFocused = false"
              />
            </div>

            <!-- 验证码输入 -->
            <div class="flex items-center justify-between">
              <div :class="['input-container bg-blue-300/20', codeFocused ? 'focused' : '']">
                <input
                  v-model="verificationCode"
                  class="input-field"
                  placeholder="请输入验证码"
                  maxlength="6"
                  @focus="codeFocused = true"
                  @blur="codeFocused = false"
                />
              </div>
              <button
                class="ml-2 px-4 py-2 text-sm font-bold flex-shrink-0 rounded-lg transition duration-300"
                :class="
                  isCountingDown || !isPhoneNumberValid
                    ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                "
                @click="sendVerificationCode"
              >
                {{ isCountingDown ? `${countdown}s重新获取` : '获取验证码' }}
              </button>
            </div>

            <!-- 协议同意框 -->
            <div class="flex items-start space-x-2">
              <input type="checkbox" v-model="isAgreed" class="mt-1" />
              <span class="text-xs text-gray-400 leading-tight">
                绑定手机号即代表您已阅读并同意
                <a class="cursor-pointer text-blue-400" @click="toUserAgreement"> 《用户协议》 </a>
                和
                <a class="cursor-pointer text-blue-400" @click="toPrivacyPolicy"> 《隐私政策》 </a>
              </span>
            </div>
          </div>

          <button
            class="mt-10 w-full py-3 text-lg font-bold text-white bg-blue-500 rounded-full transition duration-300"
            :class="{ 'opacity-50 cursor-not-allowed': !canBind }"
            @click="handleBind"
          >
            确认绑定
          </button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<style scoped>
.bind-phone-dialog {
  background: url('@/assets/images/login_bg.png') no-repeat;
  background-position: center;
  background-size: cover;
  height: 100%;
}

.title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}

.close-icon {
  font-size: 20px;
  color: #666;
  cursor: pointer;
}

.input-container {
  border: 2px solid rgba(125, 211, 252, 0);
  border-radius: 1rem;
  transition: duration-200;
}

.input-container.focused {
  border: 2px solid #3b82f6;
}

.input-field {
  width: 100%;
  padding: 1rem;
  background: transparent;
  border: none;
  outline: none;
  transition: border-color 0.3s ease;
}
</style>
